import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {TableauPage} from '@models/sidebar/response/tableau-page';
import {PageItem} from '@models/sidebar/page-item';
import {PAGE_CONTAINER_TYPE} from '@constants/sidebar/page-type.constant';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';
import {OrganizationTableauConfiguration} from '@models/organization/organization-tableau-configuration';
import {shareReplay} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TableauPageService {

  private CREATE_TABLEAU_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/tableauViews/add';
  private RENAME_TABLEAU_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/rename/baseId/:itemId';
  private DELETE_TABLEAU_PAGE_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/:viewId/baseId/:itemId/:isParentMachineGroup';
  private VALIDATE_TABLEAU_SETTINGS_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/:tenant/validateCorrectTableauConfiguration';
  private TABLEAU_VIEW_JWT_TOKEN_URL_SUFFIX = '/api/web/base/:machineOrGroupId/tableauPage/:tenant/getViewAccessJwtToken';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';

  private readonly BASE_URL: string;


  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private globalHelper: GlobalHelperService,) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating tableau page.');
    this.successCreatingPage = this.translateService.instant('Tableau page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming tableau page.');
    this.successRenamingPage = this.translateService.instant('Tableau page was renamed.');
  }

  public create(baseMenuItems: PageItem[], pageItem: PageItem, machineOrGroupId: string, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean, isMachineTab: boolean): void {
    if (parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const id = this.leftMenuService.getHighestPageIdNumber(baseMenuItems, pageItem);
      const pageId = PAGE_CONTAINER_TYPE.TABLEAU_PAGE + id;
      pageItem.pageId = pageId;
      const tableauPage = new TableauPage(pageId, pageItem.name, isMachineTab);
      this.createTableauPage(machineOrGroupId, tableauPage).subscribe({
        next: (newBaseMenuItem: BaseMenuItem) => {
          this.handleCreatedPage(newBaseMenuItem, pageItem, baseMenuItems, currentPageName, parentId, isAdminOrHigher);
        }, error: (error) => {
          this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
          this.loadingIndicator.hide();
        }
      });
    }
  }

  private createTableauPage(machineId: string, request: TableauPage): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_TABLEAU_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineId);
    return this.http.post<TableauPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], currentPageName: string, parentId: string, isAdminOrHigher: boolean) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  public rename(pageItem: PageItem, machineOrGroupId: string, parentId: string | null, currentPageName: string, isMachineTab: boolean, nameFallback?: Object): void {
    if (parentId && pageItem.id && pageItem.pageId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const tableauPage = new TableauPage(pageItem.pageId, pageItem.name, isMachineTab);
      const itemId: string = pageItem.id;
      this.renameTableauPage(machineOrGroupId, itemId, tableauPage).subscribe({
        next: (result: BaseMenuItem) => {
          this.globalHelper.handleUpdatedPage(result.name, parentId, currentPageName, itemId, this.successRenamingPage);
        }, error: (error) => {
          this.leftMenuService.broadcastErrorUpdatePageChange(<Object>nameFallback);
          this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
          this.loadingIndicator.hide();
        }
      });
    }
  }

  private renameTableauPage(machineId: string, itemId: string, request: TableauPage): Observable<any> {
    let url: string = this.BASE_URL + this.RENAME_TABLEAU_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', machineId)
    .replace(':itemId', itemId);
    return this.http.post<TableauPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public deleteTableauPage(machineId: string, itemId: string, viewId: string, isParentMachine: boolean): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_TABLEAU_PAGE_URL_SUFFIX;
    url = url.replace(':machineOrGroupId', String(machineId))
    .replace(':itemId', itemId)
    .replace(':viewId', viewId)
    .replace(':isParentMachineGroup', String(isParentMachine));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public validateCorrectTableauConfiguration(organizationId: number | null, configuration: OrganizationTableauConfiguration): Observable<any> {
    let url: string = this.BASE_URL + this.VALIDATE_TABLEAU_SETTINGS_URL_SUFFIX;
    url = url.replace(':tenant', String(organizationId))
    .replace(':machineOrGroupId', String(organizationId));
    return this.http.post<OrganizationTableauConfiguration>(url, configuration, HTTPS_REQUEST_OPTIONS);
  }

  public getViewAccessJwtToken(organizationId: number | null) {
    let url: string = this.BASE_URL + this.TABLEAU_VIEW_JWT_TOKEN_URL_SUFFIX;
    url = url.replace(':tenant', String(organizationId))
    .replace(':machineOrGroupId', String(organizationId));

   return this.http.get<string>(url, HTTPS_REQUEST_OPTIONS);
  }


}
